<template>
  <section>
    <report-filter-bar
      v-model="filter"
      :preset="preset"
      :busy="isAnyLoading"
      :compare-label="compareLabel"
      :show-paid-only="false"
      @preset="applyPreset"
      @change="refresh"
      @refresh="refresh"
    />

    <traffic-filter-bar
      v-model="filter"
      :options="options"
      @change="refresh"
    />

    <!--
      شفاف‌سازی محدودیت داده: بازدیدهای ثبت‌شده قبل از افزودن ستون SiteId
      قابل تفکیک بین سایت‌ها نیستند، و جدول Visit فقط وقتی پر می‌شود که
      تنظیم «ثبت بازدید» سایت روشن باشد.
    -->
    <b-row v-if="showTrafficNote">
      <b-col cols="12">
        <b-alert
          variant="warning"
          show
          class="mb-1"
        >
          <b-card-body class="py-75 px-1">
            <feather-icon
              icon="AlertTriangleIcon"
              size="15"
            />
            <span class="ml-50 small">
              <template v-if="!capabilities.Traffic">
                ثبت بازدید برای این سایت غیرفعال است، پس بخش «ترافیک کل سایت» خالی می‌ماند.
                بازدید محتوا (پایین صفحه) مستقل از این تنظیم ثبت می‌شود و معتبر است.
              </template>
              <template v-else-if="capabilities.TrafficSince">
                ترافیک تفکیک‌شده‌ی این سایت از {{ toJalali(capabilities.TrafficSince) }}
                به بعد ثبت شده است؛ بازه‌های قبل از آن ناقص‌اند.
              </template>
            </span>
          </b-card-body>
        </b-alert>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        cols="12"
        class="mb-1"
      >
        <report-chart
          :series="site"
          title="ترافیک کل سایت"
          subtitle="بازدید و بازدیدکننده‌ی یکتا بر اساس لاگ درخواست‌ها"
          :metrics="['views', 'visitors']"
          type="area"
          :height="310"
          :loading="loading('site')"
          :skin="skin"
          empty-text="داده‌ی ترافیک برای این بازه ثبت نشده است"
        />
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        lg="4"
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="devices"
          title="دستگاه"
          subtitle="تشخیص از User-Agent — ربات‌ها جدا شمرده می‌شوند"
          label-title="دستگاه"
          :loading="loading('devices')"
        />
      </b-col>

      <b-col
        lg="4"
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="platforms"
          title="پلتفرم"
          subtitle="سیستم‌عامل بازدیدکننده"
          label-title="پلتفرم"
          :loading="loading('platforms')"
        />
      </b-col>

      <b-col
        lg="4"
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="status"
          title="وضعیت پاسخ"
          subtitle="کد وضعیت HTTP و میانگین زمان پاسخ"
          label-title="کد"
          :loading="loading('status')"
        />
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        lg="6"
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="referrers"
          title="منابع ورودی"
          subtitle="بر اساس دامنه‌ی ارجاع‌دهنده"
          label-title="منبع"
          :loading="loading('referrers')"
          empty-text="ارجاع خارجی ثبت نشده است"
        />
      </b-col>

      <b-col
        lg="6"
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="bots"
          title="ربات‌ها"
          subtitle="خزنده‌ها، پیش‌نمایش‌سازها و اسکریپت‌ها — کاربران واقعی در این جدول نیستند"
          label-title="ربات"
          :loading="loading('bots')"
          empty-text="بازدید رباتی در این بازه ثبت نشده است"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="pages"
          title="پربازدیدترین آدرس‌ها"
          subtitle="ستون «خطا» تعداد پاسخ‌های ۴xx و ۵xx همان آدرس است"
          label-title="آدرس"
          :loading="loading('pages')"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col
        cols="12"
        class="mb-1"
      >
        <report-visit-log
          :log="log"
          :loading="loading('log')"
          :sort="logOptions.Sort"
          @page="changePage"
          @per-page="changePerPage"
          @sort="changeSort"
        />
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        cols="12"
        class="mb-1"
      >
        <report-chart
          :series="content"
          title="بازدید محتوا"
          subtitle="مستقل از تنظیم «ثبت بازدید» — همیشه معتبر"
          :metrics="['product', 'blog', 'duty', 'project']"
          type="area"
          :height="310"
          :loading="loading('content')"
          :skin="skin"
        />
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { BAlert, BCardBody } from 'bootstrap-vue'
import reportMixin from '@core/mixins/report'

export default {
  name: 'ReportTraffic',
  components: { BAlert, BCardBody },
  mixins: [reportMixin],
  data() {
    return {
      site: null,
      content: null,
      referrers: null,
      pages: null,
      devices: null,
      platforms: null,
      bots: null,
      status: null,
      log: null,
      capabilities: {},
      // گزینه‌های فیلتر؛ تا رسیدنشان نوار فیلتر خالی رندر می‌شود
      options: {},
      // صفحه‌بندی و ترتیب لاگ جدا از filter نگه داشته می‌شوند تا ورق‌زدنِ لاگ
      // کلید کش بقیه‌ی گزارش‌ها را عوض نکند
      logOptions: {
        Page: 1,
        PerPage: 25,
        Sort: 'recent',
      },
    }
  },
  computed: {
    compareLabel() {
      return this.site && this.site.CompareRange ? this.site.CompareRange.Label : null
    },

    showTrafficNote() {
      return !this.capabilities.Traffic || !!this.capabilities.TrafficSince
    },
  },
  created() {
    // فیلترهای ترافیک باید یکجا روی filter نشانده شوند تا در Vue 2 واکنشی باشند
    this.filter = {
      ...this.filter,
      PaidOnly: false,
      Audience: null,
      Devices: [],
      Platforms: [],
      Bots: [],
      UrlKinds: [],
      Statuses: [],
      ReferrerKind: null,
      Referrer: null,
      Url: null,
      UserKind: null,
      User: null,
    }

    this.refresh()

    this.$http.post(this.$api.report.capabilities, {})
      .then(res => { this.capabilities = res.data || {} })
      .catch(() => {})

    this.$http.post(this.$api.report.trafficOptions, {})
      .then(res => { this.options = res.data || {} })
      .catch(() => {})
  },
  methods: {
    refresh() {
      const api = this.$api.report

      this.fetchReport('site', api.trafficSite)
      this.fetchReport('content', api.trafficContent)
      this.fetchReport('referrers', api.trafficReferrers)
      this.fetchReport('pages', api.trafficPages)
      this.fetchReport('devices', api.trafficDevices)
      this.fetchReport('platforms', api.trafficPlatforms)
      this.fetchReport('bots', api.trafficBots)
      this.fetchReport('status', api.trafficStatus)

      // هر تغییر فیلتر یعنی مجموعه‌ی نتایج عوض شده، پس صفحه از اول
      this.logOptions.Page = 1
      this.fetchLog()
    },

    fetchLog() {
      this.fetchReport('log', this.$api.report.trafficLog, this.logOptions)
    },

    changePage(page) {
      this.logOptions.Page = page
      this.fetchLog()
    },

    changePerPage(perPage) {
      this.logOptions.PerPage = perPage
      this.logOptions.Page = 1
      this.fetchLog()
    },

    changeSort(sort) {
      this.logOptions.Sort = sort
      this.logOptions.Page = 1
      this.fetchLog()
    },
  },
}
</script>
